/**
 * bills.whip_math — whip math summary for a tracked bill (FB-HOJP2J62).
 *
 * Compact card showing supporters/cosponsors/peer alignment for a
 * specific bill. Pulls from /api/v1/workspaces/:id/bills/:billRef/whip-math.
 *
 * Params: bill_ref (required).
 */
import { useQuery } from "@tanstack/react-query";
import { DataUnavailable } from "../../../framework/react/DataUnavailable.js";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight } from "lucide-react";
import { WidgetProps } from "../registry";
import { Skeleton } from "../../components/ui";

interface WhipMath {
  bill_ref?: string;
  error?: string;
  supporter_count?: number;
  supporter_count_by_party?: Record<string, number>;
  peer_universe_size?: number;
  in_party_at_risk?: any[];
  cross_party_targets?: any[];
}

function formatBillRef(ref: string): string {
  const m = ref.match(/^(\d+)-(hr|s|hres|sres|hjres|sjres|hconres|sconres)-(\d+)$/);
  if (!m) return ref;
  const map: Record<string, string> = {
    hr: "H.R.", s: "S.", hres: "H.Res.", sres: "S.Res.",
    hjres: "H.J.Res.", sjres: "S.J.Res.", hconres: "H.Con.Res.", sconres: "S.Con.Res.",
  };
  return `${map[m[2]] ?? m[2]} ${m[3]}`;
}

export function BillsWhipMath({ spec, workspaceId }: WidgetProps) {
  const billRef = String(spec.params?.bill_ref ?? "").trim();

  const { data, isLoading, error, refetch } = useQuery<WhipMath>({
    queryKey: ["widget", "bills.whip_math", workspaceId, billRef],
    queryFn: async () => {
      const res = await fetch(
        `/api/v1/workspaces/${workspaceId}/bills/${encodeURIComponent(billRef)}/whip-math`,
        { credentials: "include" }
      );
      if (!res.ok) throw new Error("Failed to load whip math");
      return res.json();
    },
    enabled: !!billRef,
  });

  if (!billRef) return <div className="text-xs text-stone-500 italic">Set <code>params.bill_ref</code> to a tracked bill (e.g. <code>119-s-1748</code>).</div>;
  if (error) return <DataUnavailable message="Whip math could not be loaded." retry={refetch} />;
  if (isLoading) return <div role="status" aria-live="polite" className="space-y-2"><span className="sr-only">Loading whip math…</span><Skeleton className="h-3 w-full" /><Skeleton className="h-3 w-4/5" /><Skeleton className="h-3 w-3/5" /></div>;
  if (data?.error) {
    return <DataUnavailable message="Whip math could not be loaded." retry={refetch} />;
  }
  if (!data) return null;

  const byParty = data.supporter_count_by_party ?? {};
  return (
    <div className="space-y-2 text-xs">
      <div className="flex items-baseline gap-2">
        <Link to={`/bills/${encodeURIComponent(billRef)}`} className="font-mono font-semibold text-stone-900 hover:underline">{formatBillRef(billRef)}</Link>
        <span className="text-stone-500">·</span>
        <span className="text-stone-700">{data.supporter_count ?? 0} supporter{(data.supporter_count ?? 0) === 1 ? "" : "s"}</span>
      </div>
      {Object.keys(byParty).length > 0 && (
        <div className="flex items-center gap-2 text-[10px]">
          {Object.entries(byParty).map(([p, n]) => {
            const cls = p === "R" ? "bg-red-100 text-red-800" : p === "D" ? "bg-sky-100 text-sky-800" : "bg-purple-100 text-purple-800";
            return (
              <span key={p} className={`px-1.5 py-0.5 rounded font-bold ${cls}`}>
                {p}: {n}
              </span>
            );
          })}
        </div>
      )}
      {data.peer_universe_size != null && (
        <div className="text-stone-500 text-[10px]">
          Peer universe: {data.peer_universe_size} member{data.peer_universe_size === 1 ? "" : "s"}
          {(data.in_party_at_risk?.length ?? 0) > 0 && <> · <AlertTriangle size={10} aria-hidden="true" className="inline align-[-1px] text-warn" /> {data.in_party_at_risk!.length} in-party at risk</>}
          {(data.cross_party_targets?.length ?? 0) > 0 && <> · {data.cross_party_targets!.length} cross-party target{data.cross_party_targets!.length === 1 ? "" : "s"}</>}
        </div>
      )}
      <Link to={`/bills/${encodeURIComponent(billRef)}/whip-count`} className="inline-flex items-center gap-1 text-[11px] font-medium text-action hover:underline">
        Open whip board<ArrowRight size={11} aria-hidden="true" />
      </Link>
    </div>
  );
}
